import { HYDRATE } from 'next-redux-wrapper';
import { FILTER_MENU_ACTION } from 'store/action-types/filter-menu.action-types';
import { categoryChanged } from 'store/actions/search.actions';
import { Category } from 'types/category.types';
import { FILTER_MENU_ITEM } from 'types/filter-menu.types';
import { ProductCondition } from 'types/product.types';

import { Brand } from '../../types/brand.types';
import { ColorType } from '../../types/color.types';
import { parseJSON } from '../../utils/parse-json';

export type FilterMenuReducer = {
  [FILTER_MENU_ITEM.CONDITIONS]: ProductCondition[];
  [FILTER_MENU_ITEM.BRANDS]: Brand[];
  [FILTER_MENU_ITEM.COLORS]: ColorType[];
  [FILTER_MENU_ITEM.CATEGORIES]: Category[];
  selected: {
    [FILTER_MENU_ITEM.CONDITIONS]: number[];
    [FILTER_MENU_ITEM.BRANDS]: number[];
    [FILTER_MENU_ITEM.COLORS]: number[];
    [FILTER_MENU_ITEM.MATERIALS]: number[];
    [FILTER_MENU_ITEM.CATEGORY]: number;
    [FILTER_MENU_ITEM.PARENT_CATEGORY]: number;
  };
};

const initialState: FilterMenuReducer = {
  [FILTER_MENU_ITEM.CONDITIONS]: [],
  [FILTER_MENU_ITEM.BRANDS]: [],
  [FILTER_MENU_ITEM.COLORS]: [],
  [FILTER_MENU_ITEM.CATEGORIES]: [],
  selected: {
    [FILTER_MENU_ITEM.CONDITIONS]: [],
    [FILTER_MENU_ITEM.BRANDS]: [],
    [FILTER_MENU_ITEM.COLORS]: [],
    [FILTER_MENU_ITEM.MATERIALS]: [],
    [FILTER_MENU_ITEM.CATEGORY]: null,
    [FILTER_MENU_ITEM.PARENT_CATEGORY]: null,
  },
};

const filterMenuReducer = (state = initialState, action) => {
  switch (action.type) {
    case HYDRATE: {
      return { ...state, ...action.payload.filterMenu };
    }
    // SSR
    case FILTER_MENU_ACTION.CONDITIONS.INIT_SET: {
      return {
        ...state,
        [FILTER_MENU_ITEM.CONDITIONS]: [{ id: 0, name: 'pages.search.filters.all', description: '' }, ...(action?.payload || [])],
      };
    }
    case FILTER_MENU_ACTION.BRANDS.INIT_SET: {
      return {
        ...state,
        [FILTER_MENU_ITEM.BRANDS]: [...(action?.payload || [])],
      };
    }

    case FILTER_MENU_ACTION.COLORS.INIT_SET: {
      return {
        ...state,
        [FILTER_MENU_ITEM.COLORS]: [{ id: 0, name: 'pages.search.filters.all', colorValue: null }, ...(action?.payload || [])],
      };
    }

    case FILTER_MENU_ACTION.MATERIALS.INIT_SET: {
      return {
        ...state,
        [FILTER_MENU_ITEM.MATERIALS]: [{ id: 0, name: 'pages.search.filters.all', materialValue: null }, ...(action?.payload || [])],
      };
    }

    case FILTER_MENU_ACTION.CATEGORIES.INIT_SET: {
      return {
        ...state,
        [FILTER_MENU_ITEM.CATEGORIES]: [...(action?.payload || [])],
      };
    }

    case FILTER_MENU_ACTION.CONDITIONS.SELECTION_PARSED_FROM_URL_PARAMS: {
      const query = action.payload;

      const selectedProductConditionsSet = new Set<number>();
      const selectedConditions = query[FILTER_MENU_ITEM.CONDITIONS];
      const parsedSelectedConditions = parseJSON(selectedConditions) ?? [];

      if (Array.isArray(parsedSelectedConditions)) {
        parsedSelectedConditions?.forEach((id) => selectedProductConditionsSet.add(+id));
      }

      return {
        ...state,
        selected: {
          ...state.selected,
          [FILTER_MENU_ITEM.CONDITIONS]: Array.from(selectedProductConditionsSet),
        },
      };
    }

    case FILTER_MENU_ACTION.BRANDS.SELECTION_PARSED_FROM_URL_PARAMS: {
      const query = action.payload;
      const brandPageBrand = action.brandPageBrand;

      const selectedBrandsSet = new Set<number>();
      const selectedBrands = query[FILTER_MENU_ITEM.BRANDS];
      const parsedSelectedBrands = parseJSON(selectedBrands) ?? [];

      if (Array.isArray(parsedSelectedBrands)) {
        parsedSelectedBrands?.forEach((id) => selectedBrandsSet.add(+id));
      }

      if (brandPageBrand) {
        selectedBrandsSet.add(brandPageBrand.id);
      }

      return {
        ...state,
        selected: {
          ...state.selected,
          [FILTER_MENU_ITEM.BRANDS]: Array.from(selectedBrandsSet),
        },
      };
    }

    case FILTER_MENU_ACTION.COLORS.SELECTION_PARSED_FROM_URL_PARAMS: {
      const query = action.payload;

      const selectedColorsSet = new Set<number>();
      const selectedColors = query[FILTER_MENU_ITEM.COLORS];
      const parsedSelectedColors = parseJSON(selectedColors) ?? [];

      if (Array.isArray(parsedSelectedColors)) {
        parsedSelectedColors?.forEach((id) => selectedColorsSet.add(+id));
      }

      return {
        ...state,
        selected: {
          ...state.selected,
          [FILTER_MENU_ITEM.COLORS]: Array.from(selectedColorsSet),
        },
      };
    }

    case FILTER_MENU_ACTION.MATERIALS.SELECTION_PARSED_FROM_URL_PARAMS: {
      const query = action.payload;

      const selectedMaterialsSet = new Set<number>();
      const selectedMaterials = query[FILTER_MENU_ITEM.MATERIALS];
      const parsedSelectedColors = parseJSON(selectedMaterials) ?? [];

      if (Array.isArray(parsedSelectedColors)) {
        parsedSelectedColors?.forEach((id) => selectedMaterialsSet.add(+id));
      }

      return {
        ...state,
        selected: {
          ...state.selected,
          [FILTER_MENU_ITEM.MATERIALS]: Array.from(selectedMaterialsSet),
        },
      };
    }

    case categoryChanged.type: {
      if (categoryChanged.match(action)) {
        const { id, parentId } = action.payload;

        return {
          ...state,
          selected: {
            ...state.selected,
            [FILTER_MENU_ITEM.CATEGORY]: id,
            [FILTER_MENU_ITEM.PARENT_CATEGORY]: parentId,
          },
        };
      } else {
        return state;
      }
    }

    default: {
      return state;
    }
  }
};

export default filterMenuReducer;
